import { USER_ROLE } from '@trustero/trustero-api-web/lib/account/account_pb'
import {
  CAPABILITY,
  PERMISSION_RULE,
} from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { useCallback, useEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useAccount } from 'src/components/async/Account/account.hooks'
import { useUser } from 'src/components/async/Users'
import {
  AccessManagementAbsoluteRoutes,
  AiGrcQaAbsoluteRoutes,
  AuditBotAbsoluteRoutes,
  ControlsAbsoluteRoutes,
  DirectRoutes,
  EvidenceAbsoluteRoutes,
  ExamineAndTestAbsoluteRoutes,
  FrameworksAbsoluteRoutes,
  PoliciesAbsoluteRoutes,
  ReceptorsAbsoluteRoutes,
  RequestsAbsoluteRoutes,
  RisksAbsoluteRoutes,
  RoadmapAbsoluteRoutes,
  SchedulingAbsoluteRoutes,
  ScopeAbsoluteRoutes,
  SecurityQuestionnaireAbsoluteRoutes,
  ServicesAbsoluteRoutes,
  TrustPortalConfigurationAbsoluteRoutes,
  TrusteroIntelligenceAbsoluteRoutes,
  UserRolesAbsoluteRoutes,
  VendorsAbsoluteRoutes,
} from 'src/components/Reusable/RootPage/RootPage.constants'
import { getRoleConfig, PERMISSIONS, RoleConfig } from 'src/config/roleConfig'
import { useInAudit } from 'src/context/AuditContext'
import { useAuth, Permissions } from 'src/context/authContext'

export const checkPath = (routes: string[], path: string): boolean => {
  for (const route of routes) {
    const regex = new RegExp(`^/[a-z0-9:]+/${route}(?:/|$)`)
    if (regex.test(path)) {
      return true
    }
  }
  return false
}

export const isShowPageRoute = (path: string): boolean => {
  return /\/show\/[^/]+/.test(path)
}

/**
 * Every route a HAS_PAGE_ACCESS capability governs, so denying the capability hides the
 * nav entry and bounces the route. A capability only belongs here if it gates a page:
 * Chats, Playbooks, Departments and Content Versions have no page of their own - they
 * gate features inside other pages (the copilot panel, the copilot library, the
 * department pickers and the frameworks page), which their CAN_* capabilities cover.
 */
export const PAGE_ACCESS_CAPABILITY_ROUTES: Partial<
  Record<CAPABILITY, string[]>
> = {
  [CAPABILITY.HAS_PAGE_ACCESS_CONTROLS]: [ControlsAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_POLICIES]: [PoliciesAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_EVIDENCE]: [EvidenceAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_RISKS]: [RisksAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_SERVICES]: [ServicesAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_RECEPTORS]: [ReceptorsAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_FRAMEWORKS]: [FrameworksAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_AUDITS]: [DirectRoutes.AUDITS],
  [CAPABILITY.HAS_PAGE_ACCESS_REQUESTS]: [RequestsAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_REPORTS]: [DirectRoutes.REPORTS],
  [CAPABILITY.HAS_PAGE_ACCESS_ROADMAP]: [RoadmapAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_DASHBOARDS]: [
    DirectRoutes.DASHBOARD,
    DirectRoutes.GRC_DASHBOARDS,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_USERS]: [DirectRoutes.USERS],
  [CAPABILITY.HAS_PAGE_ACCESS_SCOPE]: [ScopeAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_AUDIT_SCANS]: [
    ExamineAndTestAbsoluteRoutes.ROOT,
    AuditBotAbsoluteRoutes.ROOT,
    ExamineAndTestAbsoluteRoutes.COMPLIANCE_ROOT,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_TRUST_PORTAL]: [
    TrustPortalConfigurationAbsoluteRoutes.ROOT,
    AccessManagementAbsoluteRoutes.ROOT,
  ],
  // security_questionnaire is the deprecated route the live ai_grc_qa page replaced;
  // both still resolve, so both have to be denied together.
  [CAPABILITY.HAS_PAGE_ACCESS_QUESTIONNAIRES]: [
    SecurityQuestionnaireAbsoluteRoutes.ROOT,
    AiGrcQaAbsoluteRoutes.ROOT,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_ACCOUNT_SETTINGS]: [
    DirectRoutes.ACCOUNT_SETTINGS,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_VENDORS]: [VendorsAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_COMPANY_INFO]: [DirectRoutes.COMPANY_INFO],
  [CAPABILITY.HAS_PAGE_ACCESS_SCHEDULES]: [SchedulingAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_TRUSTERO_INTELLIGENCE]: [
    TrusteroIntelligenceAbsoluteRoutes.ROOT,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_ACCOUNT_DETAILS]: [
    DirectRoutes.ACTIVITIES,
    DirectRoutes.USAGE,
    DirectRoutes.INDEXING,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_ACCESS_ROLES]: [UserRolesAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_GRC_DOCUMENTS]: [DirectRoutes.MANAGE_DOCUMENTS],
}

// The order a "land on a page this role can open" flow walks: the dashboard first
// because it is the app's home, then everything else the registry knows about.
const PAGE_ACCESS_LANDING_ORDER: CAPABILITY[] = [
  CAPABILITY.HAS_PAGE_ACCESS_DASHBOARDS,
  ...(Object.keys(PAGE_ACCESS_CAPABILITY_ROUTES).map(Number) as CAPABILITY[]),
]

/**
 * The first page route the caller's role may open, or undefined when it may open none.
 * `isPageAccessAllowed` answers for a single HAS_PAGE_ACCESS capability.
 */
export const firstAllowedPageRoute = (
  isPageAccessAllowed: (capability: CAPABILITY) => boolean,
): string | undefined => {
  for (const capability of PAGE_ACCESS_LANDING_ORDER) {
    const routes = PAGE_ACCESS_CAPABILITY_ROUTES[capability]
    if (routes?.length && isPageAccessAllowed(capability)) {
      return routes[0]
    }
  }
  return undefined
}

const checkAuditPath = (allowedAudits: string[], path: string): boolean => {
  if (path.length === 0) {
    return false
  }

  for (const audit of allowedAudits) {
    if (path.includes(audit)) {
      return true
    }
  }
  return false
}

export const useIsSuperUser = (): boolean => {
  const { email, role } = useAuth()
  return email.includes('@trustero.com') && role === USER_ROLE.ADMIN
}

export const useValidateRouteAccess = (isAuthLoading: boolean): void => {
  const {
    routeConfig: { denyRoutes },
  } = useGetRoleAccessConfig()
  const location = useLocation()

  useEffect(() => {
    if (isAuthLoading) {
      return
    }
    const path = location.pathname

    if (checkPath(denyRoutes, path) && !isShowPageRoute(path)) {
      window.location.href = DirectRoutes.ACCESS_DENIED
    }
  }, [location.pathname, denyRoutes, isAuthLoading])
}

export const useRestrictAccessToAudit = (): void => {
  const { routeConfig } = useGetRoleAccessConfig()
  const { data } = useUser()
  const params = useParams()
  const location = useLocation()
  const { auditId } = useInAudit()

  useEffect(() => {
    const pageContext = params.pageContext
    if (!pageContext?.includes(data?.getAccountid() || '')) return
    if (!routeConfig.restrictToAuditView || !data || !pageContext) return

    const allowedAudits = data.getAuditIdsList()
    const path = location.pathname
    const onAllowedRoute = checkPath(routeConfig.explicitAllowRoutes, path)
    const inAllowedAuditView = checkAuditPath(allowedAudits, auditId || '')

    if (allowedAudits.length && onAllowedRoute && !inAllowedAuditView) {
      window.location.href = `../${data.getAccountid()}:${allowedAudits[0]}/${
        DirectRoutes.AUDITS
      }${location.search}`
    }
    if (!onAllowedRoute && !inAllowedAuditView) {
      if (allowedAudits.length > 0) {
        const targetPath = `../${data.getAccountid()}:${allowedAudits[0]}/${
          DirectRoutes.AUDITS
        }`
        if (location.pathname !== targetPath) {
          window.location.href = `${targetPath}${location.search}`
        }
      } else {
        if (location.pathname !== `${DirectRoutes.AUDITS}`) {
          window.location.href = `../${data.getAccountid()}/${
            DirectRoutes.AUDITS
          }${location.search}`
        }
      }
    }
  }, [
    routeConfig,
    data,
    params.pageContext,
    auditId,
    location.pathname,
    location.search,
  ])
}

export const useGetRoleAccessConfig = (): RoleConfig => {
  const authCtx = useAuth()
  const { data: user, isLoading } = useUser()

  const { data: account } = useAccount()

  return useMemo(() => {
    const baseConfig = getRoleConfig({
      userRole: authCtx.role,
      accountPurpose: account?.getPurpose(),
      hasQc: user?.getAllowQc(),
      isLoading,
    })

    if (!authCtx.permissions || authCtx.isAuthLoading) {
      return baseConfig
    }

    // Accounts not yet migrated to the new auth system answer getPermissions with no
    // capabilities at all; those keep the base route config rather than losing every page.
    const capabilities = authCtx.permissions.capabilities || {}
    if (Object.keys(capabilities).length === 0) {
      return baseConfig
    }

    const permissionDeniedRoutes: string[] = []

    for (const [capStr, routes] of Object.entries(
      PAGE_ACCESS_CAPABILITY_ROUTES,
    )) {
      const cap = Number(capStr) as CAPABILITY
      const capInfo = capabilities[cap]
      // Absence is denial, same as checkCanPerformAction and the backend's
      // HasCapabilities: a role on a domain's *_NONE permission set carries no entry
      // for that domain's page at all, only an explicit grant makes the page reachable.
      if (capInfo && capInfo.rule === PERMISSION_RULE.NEVER) {
        permissionDeniedRoutes.push(...(routes || []))
      }
    }

    if (permissionDeniedRoutes.length === 0) {
      return baseConfig
    }

    return {
      ...baseConfig,
      routeConfig: {
        ...baseConfig.routeConfig,
        denyRoutes: Array.from(
          new Set([
            ...baseConfig.routeConfig.denyRoutes,
            ...permissionDeniedRoutes,
          ]),
        ),
        hideNavRoutes: Array.from(
          new Set([
            ...baseConfig.routeConfig.hideNavRoutes,
            ...permissionDeniedRoutes,
          ]),
        ),
      },
    }
  }, [
    authCtx.role,
    authCtx.permissions,
    authCtx.isAuthLoading,
    account,
    user,
    isLoading,
  ])
}

export const useHasRequiredPermissions = (
  requiredPermissions: PERMISSIONS[],
): boolean => {
  const { permissionsConfig } = useGetRoleAccessConfig()

  for (const permission of requiredPermissions) {
    if (!permissionsConfig.includes(permission)) {
      return false
    }
  }

  return true
}

export const useHasEditPermissions = (): boolean => {
  return useHasRequiredPermissions([PERMISSIONS.EDIT])
}

export type ResourceWithAuthz = {
  ownerEmail?: string
  ownerId?: string
  assigneeEmailsList?: string[]
  assigneeEmails?: string[]
  labelIdsList?: string[]
  labelIds?: string[]
  getOwnerEmail?: () => string
  getOwnerId?: () => string
  getAssigneeEmailsList?: () => string[]
  getLabelIdsList?: () => string[]
}

export const checkCanPerformAction = <T extends ResourceWithAuthz>(
  capability: CAPABILITY,
  resource?: T,
  permissions?: Permissions | null,
  user?: {
    getEmail?: () => string
    getId?: () => string
  } | null,
): boolean => {
  if (!permissions) return false

  const capabilityInfo = permissions?.capabilities?.[capability]
  if (!capabilityInfo) return false

  // User Role ALWAYS and NEVER should never be affected by label permission.
  if (capabilityInfo.rule === PERMISSION_RULE.ALWAYS) {
    return true
  }
  if (capabilityInfo.rule === PERMISSION_RULE.NEVER) {
    return false
  }

  const getResourceLabels = (): string[] => {
    if (!resource) return []
    if (typeof resource.getLabelIdsList === 'function') {
      return resource.getLabelIdsList() || []
    }
    return resource.labelIdsList || resource.labelIds || []
  }

  const getResourceOwnerEmail = (): string | undefined => {
    if (!resource) return undefined
    if (typeof resource.getOwnerEmail === 'function') {
      return resource.getOwnerEmail()
    }
    return resource.ownerEmail
  }

  const getResourceOwnerId = (): string | undefined => {
    if (!resource) return undefined
    if (typeof resource.getOwnerId === 'function') {
      return resource.getOwnerId()
    }
    return resource.ownerId
  }

  const getResourceAssignees = (): string[] => {
    if (!resource) return []
    if (typeof resource.getAssigneeEmailsList === 'function') {
      return resource.getAssigneeEmailsList() || []
    }
    return resource.assigneeEmailsList || resource.assigneeEmails || []
  }

  const resourceLabelIds = getResourceLabels()
  const userNoAccess = new Set(permissions.noAccessLabelIds || [])
  const userReadOnly = new Set(permissions.readOnlyLabelIds || [])
  const userFullAccess = new Set(permissions.fullAccessLabelIds || [])

  const hasNoAccessLabel = resourceLabelIds.some((id) => userNoAccess.has(id))
  const hasFullAccessLabel = resourceLabelIds.some((id) =>
    userFullAccess.has(id),
  )
  const hasReadOnlyLabel = resourceLabelIds.some((id) => userReadOnly.has(id))

  const isViewCapability =
    capabilityInfo?.capabilityName?.startsWith('CAN_VIEW_') ||
    capability === CAPABILITY.CAN_VIEW_CONTROLS ||
    capability === CAPABILITY.CAN_VIEW_POLICIES ||
    capability === CAPABILITY.CAN_VIEW_EVIDENCE ||
    capability === CAPABILITY.CAN_VIEW_RISKS ||
    capability === CAPABILITY.CAN_VIEW_SERVICES ||
    capability === CAPABILITY.CAN_VIEW_RECEPTORS ||
    capability === CAPABILITY.CAN_VIEW_FRAMEWORKS ||
    capability === CAPABILITY.CAN_VIEW_AUDITS ||
    capability === CAPABILITY.CAN_VIEW_REQUESTS ||
    capability === CAPABILITY.CAN_VIEW_REPORTS ||
    capability === CAPABILITY.CAN_VIEW_ROADMAP ||
    capability === CAPABILITY.CAN_VIEW_DASHBOARDS ||
    capability === CAPABILITY.CAN_VIEW_USERS ||
    capability === CAPABILITY.CAN_VIEW_SCOPE ||
    capability === CAPABILITY.CAN_VIEW_ACTIVITY_LOG ||
    capability === CAPABILITY.CAN_VIEW_AUDIT_SCANS ||
    capability === CAPABILITY.CAN_VIEW_CHATS ||
    capability === CAPABILITY.CAN_VIEW_QUESTIONNAIRES ||
    capability === CAPABILITY.CAN_VIEW_ACCOUNT_SETTINGS ||
    capability === CAPABILITY.CAN_VIEW_VENDORS ||
    capability === CAPABILITY.CAN_VIEW_GRC_DOCUMENTS ||
    capability === CAPABILITY.CAN_VIEW_PLAYBOOKS ||
    capability === CAPABILITY.CAN_VIEW_ACCESS_ROLES ||
    capability === CAPABILITY.CAN_VIEW_DEPARTMENTS ||
    capability === CAPABILITY.CAN_VIEW_COMPANY_INFO ||
    capability === CAPABILITY.CAN_VIEW_SCHEDULES ||
    capability === CAPABILITY.CAN_VIEW_CONTENT_VERSIONS

  // 2. IF_DENIED_PERMISSION ("Allow (Deny by Label)"): allowed by default, denied if the resource
  // has any label associated with the user/group.
  if (capabilityInfo.rule === PERMISSION_RULE.IF_DENIED_PERMISSION) {
    if (hasNoAccessLabel || hasReadOnlyLabel || hasFullAccessLabel) {
      return false
    }
    return true
  }

  // 3. If resource has any label restricted to No Access, deny.
  if (hasNoAccessLabel) {
    return false
  }

  // 4. If resource has a label restricted to Read Only (and not elevated by Full Access),
  // deny any mutating/editing action.
  if (!isViewCapability && hasReadOnlyLabel && !hasFullAccessLabel) {
    return false
  }

  // 5. Explicit label grants
  if (hasFullAccessLabel) {
    return true
  }

  if (isViewCapability && hasReadOnlyLabel) {
    return true
  }

  // 4. Evaluate capability rule
  if (capabilityInfo.rule === PERMISSION_RULE.IF_OWNER) {
    const ownerEmail = getResourceOwnerEmail()
    const ownerId = getResourceOwnerId()
    const userEmail =
      typeof user?.getEmail === 'function' ? user.getEmail() : undefined
    const userId = typeof user?.getId === 'function' ? user.getId() : undefined
    return (
      (!!ownerEmail && ownerEmail === userEmail) ||
      (!!ownerId && ownerId === userId)
    )
  }
  if (capabilityInfo.rule === PERMISSION_RULE.IF_OWNER_OR_ASSIGNEE) {
    const ownerEmail = getResourceOwnerEmail()
    const ownerId = getResourceOwnerId()
    const userEmail =
      typeof user?.getEmail === 'function' ? user.getEmail() : undefined
    const userId = typeof user?.getId === 'function' ? user.getId() : undefined
    const isOwner =
      (!!ownerEmail && ownerEmail === userEmail) ||
      (!!ownerId && ownerId === userId)
    const isAssignee = !!userEmail && getResourceAssignees().includes(userEmail)
    return isOwner || isAssignee
  }
  if (capabilityInfo.rule === PERMISSION_RULE.IF_GRANTED_PERMISSION) {
    return false
  }

  return false
}

export const useCanPerformActionChecker = (): (<T extends ResourceWithAuthz>(
  capability: CAPABILITY,
  resource?: T,
) => boolean) => {
  const { permissions } = useAuth()
  const { data: user } = useUser()

  return useCallback(
    (capability: CAPABILITY, resource?: ResourceWithAuthz) =>
      checkCanPerformAction(capability, resource, permissions, user),
    [permissions, user],
  )
}

export const useCanPerformAction = <T extends ResourceWithAuthz>(
  capability: CAPABILITY,
  resource?: T,
): boolean => {
  const check = useCanPerformActionChecker()
  return check(capability, resource)
}
